import { api } from "@/lib/api";

const SMALL_LIMIT = 5 * 1024 * 1024;
const CHUNK_SIZE = 5 * 1024 * 1024;
const CHUNK_TIMEOUT = 5 * 60 * 1000;

const uuid = () =>
  (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const MAX_DIM = 1920;
async function compressImage(file) {
  if (!file.type.startsWith("image/") || file.type === "image/gif" || file.size < 400 * 1024) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, MAX_DIM / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * scale);
    const h = Math.round(bmp.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
    bmp.close();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.82));
    if (!blob || blob.size >= file.size) return file;
    const name = file.name.replace(/\.(heic|heif|png|webp|bmp|tiff?)$/i, ".jpg").replace(/\.jpe?g$/i, ".jpg");
    return new File([blob], name.endsWith(".jpg") ? name : `${name}.jpg`, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

async function postChunk(uploadId, index, blob, onUploadProgress) {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("index", String(index));
      fd.append("chunk", blob);
      await api.post("/upload/chunk", fd, { timeout: CHUNK_TIMEOUT, onUploadProgress });
      return;
    } catch (e) {
      if (attempt === 2) throw e;
      await sleep(1500 * (attempt + 1));
    }
  }
}

export async function uploadSmart(fileList, onProgress, opts = {}) {
  let files = Array.from(fileList);
  if (opts.compressImages) {
    onProgress?.({ percent: 0, label: "Fotoğraflar sıkıştırılıyor..." });
    files = await Promise.all(files.map(compressImage));
  }
  const small = files.filter((f) => f.size <= SMALL_LIMIT);
  const large = files.filter((f) => f.size > SMALL_LIMIT);
  const totalBytes = files.reduce((a, f) => a + f.size, 0) || 1;
  let doneBytes = 0;
  const report = (currentLoaded, label) => {
    const percent = Math.min(100, Math.round(((doneBytes + currentLoaded) / totalBytes) * 100));
    onProgress?.({ percent, label });
  };
  const out = [];
  if (small.length > 0) {
    const fd = new FormData();
    small.forEach((f) => fd.append("files", f));
    const smallTotal = small.reduce((a, f) => a + f.size, 0);
    const label = small.length > 1 ? `${small.length} dosya yükleniyor` : small[0].name;
    report(0, label);
    const { data } = await api.post("/upload", fd, {
      timeout: CHUNK_TIMEOUT,
      onUploadProgress: (e) => report(Math.min(e.loaded, smallTotal), label),
    });
    doneBytes += smallTotal;
    report(0, label);
    out.push(...data);
  }
  for (const f of large) {
    const uploadId = uuid();
    const total = Math.ceil(f.size / CHUNK_SIZE);
    const sliceAt = (i) => f.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    for (let i = 0; i < total; i++) {
      const chunk = sliceAt(i);
      await postChunk(uploadId, i, chunk, (e) => report(Math.min(e.loaded, chunk.size), f.name));
      doneBytes += chunk.size;
      report(0, f.name);
    }
    const finalizeBody = {
      upload_id: uploadId,
      filename: f.name,
      content_type: f.type || "application/octet-stream",
      total_chunks: total,
    };
    let data;
    try {
      ({ data } = await api.post("/upload/finalize", finalizeBody));
    } catch (e) {
      const { data: st } = await api.get(`/upload/status/${uploadId}`);
      const have = new Set(st.indexes || []);
      for (let i = 0; i < total; i++) {
        if (!have.has(i)) await postChunk(uploadId, i, sliceAt(i), () => report(0, `${f.name} (eksik parça ${i + 1})`));
      }
      ({ data } = await api.post("/upload/finalize", finalizeBody));
    }
    out.push(data);
  }
  onProgress?.({ percent: 100, label: "Tamamlandı" });
  return out;
}
